import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bell, Check, X, Loader2, ChevronLeft, ChevronRight, AlertTriangle, Info, AlertCircle } from 'lucide-react';

interface BillingAlert {
  id: string;
  alertType: string;
  entityType: string;
  entityId: string;
  accountId: string | null;
  accountName: string | null;
  title: string;
  message: string | null;
  severity: string;
  isRead: boolean;
  isDismissed: boolean;
  dueDate: string | null;
  createdAt: string;
}

const severityIcon: Record<string, React.ReactNode> = {
  critical: <AlertCircle className="h-5 w-5 text-destructive" />,
  warning: <AlertTriangle className="h-5 w-5 text-orange-500" />,
  info: <Info className="h-5 w-5 text-blue-500" />,
};

const severityVariant: Record<string, 'destructive' | 'warning' | 'secondary'> = {
  critical: 'destructive',
  warning: 'warning',
  info: 'secondary',
};

const alertTypeLabel: Record<string, string> = {
  overdue_invoice: 'Overdue Invoice',
  upcoming_billing: 'Upcoming Billing',
  payment_reminder_sent: 'Reminder Sent',
  contract_expiring: 'Contract Expiring',
  payment_received: 'Payment Received',
};

export function AlertsPage() {
  const queryClient = useQueryClient();
  const [alertType, setAlertType] = useState('');
  const [isReadFilter, setIsReadFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['alerts', { page, alertType, isRead: isReadFilter }],
    queryFn: () =>
      api.getAlerts({
        page,
        limit: 20,
        alertType: alertType || undefined,
        isRead: isReadFilter === '' ? undefined : isReadFilter === 'true',
      }),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.markAlertRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertCounts'] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => api.dismissAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertCounts'] });
    },
  });

  const alerts = (data?.data || []) as BillingAlert[];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Billing Alerts</h1>
        <p className="text-muted-foreground">Notifications for overdue invoices, upcoming billing, and more</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={alertType} onValueChange={(v) => { setAlertType(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="overdue_invoice">Overdue Invoice</SelectItem>
            <SelectItem value="upcoming_billing">Upcoming Billing</SelectItem>
            <SelectItem value="payment_reminder_sent">Reminder Sent</SelectItem>
            <SelectItem value="contract_expiring">Contract Expiring</SelectItem>
            <SelectItem value="payment_received">Payment Received</SelectItem>
          </SelectContent>
        </Select>
        <Select value={isReadFilter} onValueChange={(v) => { setIsReadFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All alerts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="false">Unread</SelectItem>
            <SelectItem value="true">Read</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Alerts List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : alerts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bell className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No alerts</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {alerts.map((alert) => (
            <Card key={alert.id} className={alert.isRead ? 'opacity-60' : ''}>
              <CardContent className="flex items-start gap-4 py-4">
                <div className="mt-0.5">{severityIcon[alert.severity] || severityIcon.info}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{alert.title}</span>
                    <Badge variant={severityVariant[alert.severity] || 'secondary'} className="text-xs">
                      {alert.severity}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {alertTypeLabel[alert.alertType] || alert.alertType}
                    </Badge>
                    {!alert.isRead && (
                      <span className="h-2 w-2 rounded-full bg-blue-500" />
                    )}
                  </div>
                  {alert.message && (
                    <p className="text-sm text-muted-foreground mb-1">{alert.message}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {alert.accountName && <span>{alert.accountName}</span>}
                    {alert.dueDate && <span>Due: {formatDate(alert.dueDate)}</span>}
                    <span>{formatDate(alert.createdAt)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!alert.isRead && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => markReadMutation.mutate(alert.id)}
                      title="Mark as read"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissMutation.mutate(alert.id)}
                    title="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination.total} alert{pagination.total !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page} of {pagination.totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
